import {useEffect, useMemo, useState} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Divider, IconButton, Stack, styled, Tooltip, Typography, useTheme} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {ExpandMore, FilterList, RotateLeft} from "@mui/icons-material";
import {apiGet} from "../utils";
import {
    buildDefaultDateFilters,
    accountExistsInTree,
    filterAccountTreeByBusinessUnit,
    filterProductCategoryTreeByBusinessUnit,
    parseLocationTreeSelection,
    productCategoryExistsInTree
} from "../utils/filters";
import {DateRangePicker} from "./filters/DateRangePicker";
import {GranularityPicker} from "./filters/GranularityPicker";
import {BusinessUnitPicker} from "./filters/BusinessUnitPicker";
import {AccountPicker} from "./filters/AccountPicker";
import {LocationPicker} from "./filters/LocationPicker";
import {ProductPicker} from "./filters/ProductPicker";

export const FiltersAccordion = ({filters, filtersSetter, showFilters}) => {
    const theme = useTheme();
    const dims = theme.dimensions;
    const [allAccountHierarchy, setAllAccountHierarchy] = useState([]);
    const [allProductData, setAllProductData] = useState([]);
    const [businessUnitData, setBusinessUnitData] = useState([]);
    const [locationData, setLocationData] = useState([]);

    const accountData = useMemo(
        () => filterAccountTreeByBusinessUnit(allAccountHierarchy, filters.businessUnitId),
        [allAccountHierarchy, filters.businessUnitId]
    );

    const productData = useMemo(
        () => filterProductCategoryTreeByBusinessUnit(allProductData, filters.businessUnitId),
        [allProductData, filters.businessUnitId]
    );

    useEffect(() => {
        apiGet(`list/all`, (resp) => {
            setAllAccountHierarchy(resp.data.accountHierarchy || []);
            setAllProductData(resp.data.productCategoryHierarchy);
            setBusinessUnitData(resp.data.businessUnits || []);
            setLocationData(resp.data.locationHierarchy);

            filtersSetter((current) => {
                if (current.currentPeriodStart) {
                    return current;
                }
                return buildDefaultDateFilters();
            });
        });
    }, [filtersSetter]);

    useEffect(() => {
        filtersSetter((current) => {
            const updates = {};
            if (current.accountId && !accountExistsInTree(accountData, current.accountId)) {
                updates.accountId = null;
            }
            if (current.productCategoryId && !productCategoryExistsInTree(productData, current.productCategoryId)) {
                updates.productCategoryId = null;
            }
            if (Object.keys(updates).length === 0) {
                return current;
            }
            return {...current, ...updates};
        });
    }, [accountData, productData, filtersSetter]);

    const selectCurrentPeriod = (start, end) => {
        filtersSetter((current) => ({...current, currentPeriodStart: start, currentPeriodEnd: end}));
    };

    const selectPriorPeriod = (start, end) => {
        filtersSetter((current) => ({...current, priorPeriodStart: start, priorPeriodEnd: end}));
    };

    const selectFilterGranularity = (granularity) => {
        filtersSetter((current) => ({...current, granularity}));
    };

    const selectFilterBusinessUnit = (selectedId) => {
        const businessUnitId = selectedId === 'ALL' ? null : Number(selectedId);
        filtersSetter((current) => ({
            ...current,
            businessUnitId,
            accountId: null,
            productCategoryId: null
        }));
    };

    const selectFilterAccount = (selectedId) => {
        filtersSetter((current) => ({
            ...current,
            accountId: selectedId === 'ALL' ? null : selectedId
        }));
    };

    const selectFilterLocation = (selectedId) => {
        const selection = parseLocationTreeSelection(selectedId);
        if (selection.type === 'all') {
            filtersSetter((current) => ({...current, locationId: null, salesPersonId: null}));
            return;
        }
        if (selection.type === 'salesPerson') {
            filtersSetter((current) => ({...current, locationId: null, salesPersonId: selection.salesPersonId}));
            return;
        }
        filtersSetter((current) => ({...current, locationId: selection.locationId, salesPersonId: null}));
    };

    const selectFilterProduct = (selectedId) => {
        filtersSetter((current) => ({
            ...current,
            productCategoryId: selectedId === 'ALL' ? null : selectedId
        }));
    };

    const resetFilters = () => {
        filtersSetter(buildDefaultDateFilters());
    };

    const StyledAccordionSummary = styled(AccordionSummary)({
        '& .Mui-expanded': {
            marginTop: 0,
            marginBottom: 0
        }
    });

    return (
        showFilters ?
            <div style={{marginLeft: dims.navDrawerWidth + 75, marginRight: dims.navDrawerWidth + 10}}>
                <Accordion>
                    <StyledAccordionSummary expandIcon={<ExpandMore/>}>
                        <Stack alignItems="center" direction="row" gap={2}>
                            <FilterList/>
                            <Typography variant="h6">Filters</Typography>
                        </Stack>
                    </StyledAccordionSummary>
                    <AccordionDetails style={{paddingTop: 0}}>
                        <div>
                            <Divider style={{marginBottom: 10}}/>
                            <Grid container spacing={1}>
                                    <Grid size={5}>
                                        <DateRangePicker
                                            label="Current Period"
                                            start={filters.currentPeriodStart}
                                            end={filters.currentPeriodEnd}
                                            onChange={selectCurrentPeriod}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <BusinessUnitPicker
                                            businessUnits={businessUnitData}
                                            businessUnitSetter={selectFilterBusinessUnit}
                                            filters={filters}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <AccountPicker
                                            accounts={accountData}
                                            businessUnits={businessUnitData}
                                            accountSetter={selectFilterAccount}
                                            filters={filters}
                                        />
                                    </Grid>
                                    <Grid size={1}>
                                        <Tooltip title="Reset filters">
                                            <IconButton color="inherit" style={{marginTop: 4, height: 25, float: "right"}}
                                                        onClick={resetFilters}>
                                                <RotateLeft/>
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>

                                    <Grid size={5}>
                                        <DateRangePicker
                                            label="Prior Period"
                                            start={filters.priorPeriodStart}
                                            end={filters.priorPeriodEnd}
                                            onChange={selectPriorPeriod}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <LocationPicker
                                            locations={locationData}
                                            locationSetter={selectFilterLocation}
                                            filters={filters}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <ProductPicker
                                            products={productData}
                                            businessUnits={businessUnitData}
                                            productSetter={selectFilterProduct}
                                            filters={filters}
                                        />
                                    </Grid>

                                    <Grid size={5}>
                                        <GranularityPicker granularitySetter={selectFilterGranularity} filters={filters}/>
                                    </Grid>
                            </Grid>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </div>
            :
            <div/>
    );
};
